import React, { type PropsWithChildren, useMemo } from 'react';

import { type BootstrapState, initialState } from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import { type BootstrapContainerProps, type QueryParamsCallback } from '../../index';
import { StaticBootstrapContext } from '../index';

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	'queryParams' | 'referrer' | 'prevQueryParams' | 'queryParamsCallback'
> & {
	// Added the callback wasnt being stored in state (good) and now is in static context, and is not undefined, because we always set noop in state initialiser
	queryParamsCallback: QueryParamsCallback;
};

// Attempting to build from the same underlying definitions to ease migration
export const defaultStaticContext = {
	...initialState,
	// So users of the context know it isn't ever undefined
	queryParamsCallback: () => {},
};

export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParams,
	queryParamsCallback,
}: PropsWithChildren<BootstrapContainerProps>): React.JSX.Element => {
	// Do not change this! This is what enforces only a single rerender when config is ready
	const staticLock = props.isConfigLoaded;

	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		const parsedQueryParams = queryParams && parseQueryParams(queryParams);
		return {
			cloudId: props.cloudId || defaultStaticContext.cloudId,
			aggregatorHostName: props.aggregatorHostName || defaultStaticContext.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || defaultStaticContext.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || defaultStaticContext.SAINHostName,
			orgId: props.orgId || defaultStaticContext.orgId,
			primaryProduct: props.primaryProduct || defaultStaticContext.primaryProduct,
			analyticsAttributes: props.analyticsAttributes || defaultStaticContext.analyticsAttributes,
			features: props.features || defaultStaticContext.features,
			intercomHmac: props.intercomHmac || defaultStaticContext.intercomHmac,
			experimentConfig: props.experimentConfig || defaultStaticContext.experimentConfig,
			experimentLayers: props.experimentLayers || defaultStaticContext.experimentLayers,
			isNounsAvailable: props.isNounsAvailable || defaultStaticContext.isNounsAvailable,
			isRovoEnabled: props.isRovoEnabled || defaultStaticContext.isRovoEnabled,
			isConfigLoaded: props.isConfigLoaded || defaultStaticContext.isConfigLoaded,
			nouns: props.nouns || defaultStaticContext.nouns,
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,
			productEdition: props.productEdition || defaultStaticContext.productEdition,
			productDataRegions: props.productDataRegions || defaultStaticContext.productDataRegions,
			rovoFeatures: props.rovoFeatures || defaultStaticContext.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || defaultStaticContext.linkedCloudARIs,
			isJiraIssueIndexed: props.isJiraIssueIndexed || defaultStaticContext.isJiraIssueIndexed,
			user: props.user || defaultStaticContext.user,
			searchSessionId: props.searchSessionId || crypto.randomUUID(),
			thirdPartyConfigs: props.thirdPartyConfigs || defaultStaticContext.thirdPartyConfigs,
			products: props.products || defaultStaticContext.products,
			initialSpaceARIs: props.initialSpaceARIs || defaultStaticContext.initialSpaceARIs,
			// TODO This seems like a problem
			initialTextQueryParam: parsedQueryParams?.text || defaultStaticContext.initialTextQueryParam,
			onSearchCompleted: props.onSearchCompleted || defaultStaticContext.onSearchCompleted,
			// Callbacks are considered statically defined too!
			queryParamsCallback: queryParamsCallback || defaultStaticContext.queryParamsCallback
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [staticLock]);

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			{children}
		</StaticBootstrapContext.Provider>
	);
};
